import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [email, setEmail] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setIsSubmitted(true);
      toast.success("Password reset instructions sent");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to process request";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="forgot-password-page">
      {/* Left side - Background image */}
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1767294274634-613a3545e36d?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      >
        <div className="absolute inset-0 bg-slate-900/60"></div>
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h1 className="text-4xl font-semibold tracking-tight mb-4">
            TaskForge
          </h1>
          <p className="text-lg text-slate-200 max-w-md">
            Manufacturing project management built for teams that build things.
            Track projects, suppliers, materials, and operations in one place.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm animate-slide-up">
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to login
          </Link>

          {!isSubmitted ? (
            <>
              <div className="mb-8">
                <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center mb-6">
                  <Mail className="w-6 h-6 text-slate-600" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900 tracking-tight">
                  Forgot password?
                </h2>
                <p className="text-slate-500 mt-2">
                  Enter your email and we'll send you instructions to reset your password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    className="rounded-sm h-11"
                    data-testid="forgot-email-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-sm h-11 bg-slate-900 hover:bg-slate-800"
                  data-testid="forgot-submit-btn"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Sending...
                    </div>
                  ) : (
                    "Send reset instructions"
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900 tracking-tight mb-2">
                Check your email
              </h2>
              <p className="text-slate-500 mb-6">
                If an account exists for <strong>{email}</strong>, you will receive password reset instructions via email shortly. The link expires in 1 hour.
              </p>

              <div className="space-y-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsSubmitted(false);
                    setEmail("");
                  }}
                  className="w-full rounded-sm"
                >
                  Try another email
                </Button>
                <Link to="/login">
                  <Button
                    variant="ghost"
                    className="w-full rounded-sm text-slate-600"
                  >
                    Return to login
                  </Button>
                </Link>
              </div>
            </div>
          )}

          <p className="text-center text-sm text-slate-500 mt-8">
            Remember your password?{" "}
            <Link to="/login" className="text-slate-900 font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
